import {
    createContext,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

/**
 * Profil utilisateur normalisé, agnostique du provider auth.
 * Dérive les champs d'affichage depuis user_metadata (Google) ou email.
 */
export interface AuthProfile {
    id: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
    raw: User;
}

interface AuthContextValue {
    user: AuthProfile | null;
    session: Session | null;
    loading: boolean;
    signInWithEmail: (email: string, password: string) => Promise<void>;
    signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function toProfile(user: User | null | undefined): AuthProfile | null {
    if (!user) return null;
    const meta = user.user_metadata ?? {};
    const displayName: string | null =
        meta.display_name ??
        meta.full_name ??
        meta.name ??
        null;
    const photoURL: string | null =
        meta.avatar_url ??
        meta.picture ??
        null;
    return {
        id: user.id,
        email: user.email ?? null,
        displayName,
        photoURL,
        raw: user,
    };
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<AuthProfile | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // 1. Récupère la session stockée (si elle existe) au 1er montage
        supabase.auth.getSession().then(({ data }) => {
            setSession(data.session);
            setUser(toProfile(data.session?.user));
            setLoading(false);
        });

        // 2. S'abonne aux changements d'auth (login, logout, token refresh)
        const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
            setSession(s);
            setUser(toProfile(s?.user));
            setLoading(false);
        });

        return () => sub.subscription.unsubscribe();
    }, []);

    const signInWithEmail = async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
    };

    const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
        const { error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: displayName ? { display_name: displayName } : undefined,
            },
        });
        if (error) throw error;
    };

    const signInWithGoogle = async () => {
        // Redirige vers Google puis revient sur /app/dashboard après auth.
        // L'URL de redirection doit être whitelistée dans Supabase :
        // Dashboard → Authentication → URL Configuration → Redirect URLs
        const redirectTo = `${window.location.origin}/app/dashboard`;
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo,
                queryParams: { prompt: 'select_account' },
            },
        });
        if (error) throw error;
    };

    const logout = async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    };

    const value: AuthContextValue = {
        user,
        session,
        loading,
        signInWithEmail,
        signUpWithEmail,
        signInWithGoogle,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth doit être utilisé à l\'intérieur d\'un <AuthProvider>');
    }
    return ctx;
}
